import React from "react";
import { Box, Stack } from "@mui/material";

import { AdditionalImageSeriesWidgetData } from "@/types/additional-widget-data";
import { default as ImageComponent } from "@/features/shared/components/image/image";
import { ImageSelector } from "@/features/shared/widgets/components";
import { LegacyWidgetConfiguration } from "@/lib/api";
import { Players } from "@/types/widget";
import useRefetchWidget from "@/features/shared/widgets/hooks/useRefetchWidget";

import Player from "./player";

type Props = {
  id: string;
  data: AdditionalImageSeriesWidgetData;
  configuration: LegacyWidgetConfiguration & {
    settings: {
      player: Players;
    };
  };
};

const ImageSeries = ({ id, data, configuration }: Props) => {
  useRefetchWidget(id);

  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);

  const serie = data.data?.[0];
  const images = serie.data;

  const onPlayerValueChange = React.useCallback(
    (value: number) => {
      setSelectedImageIndex(Math.round((Number(value) / 100) * images.length));
    },
    [images],
  );

  const selectedImage = React.useMemo(
    () => images?.[selectedImageIndex],
    [images, selectedImageIndex],
  );

  const playerValue = React.useMemo(() => {
    return (selectedImageIndex / images.length) * 100;
  }, [selectedImageIndex, images]);

  const showPlayer = configuration.settings.player !== Players.NONE;
  const showSelector =
    configuration.settings.player === Players.SIMPLE_SELECTOR || images.length > 1;

  let negatedHeight = 0;
  if (showPlayer) negatedHeight += 56;
  if (showSelector) negatedHeight += 45;

  if (images.length === 0) return null;

  return (
    <Box display="flex" height="100%" flexDirection="column">
      <Stack gap={1.5} direction="column" height="100%">
        {showSelector && (
          <ImageSelector
            images={images}
            selectedImageIndex={selectedImageIndex}
            onSelectedImageChange={setSelectedImageIndex}
          />
        )}

        {selectedImage && (
          <div
            style={{
              maxHeight: `calc(100% - ${negatedHeight}px)`,
            }}
          >
            <ImageComponent
              {...selectedImage}
              title={selectedImage.name}
              src={selectedImage.url}
            />
          </div>
        )}

        {showPlayer && (
          <Player
            currentTick={playerValue}
            onTickChange={onPlayerValueChange}
            maxTicks={images.length}
          />
        )}
      </Stack>
    </Box>
  );
};

export default ImageSeries;
